/**
 * Planning-actie-status — een monteur-gerichte "wat moet ik doen"-signaalwaarde per
 * planning-dagitem. PURE PRESENTATIE: dit is een signaal, NOOIT een lock. De echte
 * edit-lock zit op werkbon-status + dag-status server-side (zie werkbonnen-spec).
 *
 * Gedeeld tussen dag-/week-/maandweergave in de portal zodat de mapping nergens
 * gedupliceerd wordt. Statussen komen als platte strings binnen (geen import uit
 * @streep/types) zodat dit pakket dependency-vrij blijft.
 */

export type PlanningActieStatus =
  | 'KOMT_NOG' // gepland, geplande starttijd nog niet geweest (toekomst); label "Gepland"
  | 'NU_INVULLEN' // gepland, starttijd verstreken (of dag vandaag/verleden), nog geen actuals
  | 'INGEVULD' // dag ACTIEF: werkelijke tijden ingevuld, nog aanpasbaar, nog NIET ingediend
  | 'CORRIGEREN' // heropend of afgekeurd: monteur moet iets aanpassen
  | 'WACHT_GOEDKEURING' // ingediend, goedkeuringsflow loopt - uit handen van de monteur
  | 'KLAAR' // klant akkoord (GOEDGEKEURD_KLANT) - afgesloten

export interface PlanningActieStatusInput {
  /** GEPLAND | ACTIEF | GESLOTEN | HEROPEND */
  dayEntryStatus: string
  /** WACHT_OP_INTERN | GOEDGEKEURD_INTERN | AFGEKEURD_INTERN | WACHT_OP_KLANT | GOEDGEKEURD_KLANT | AFGEKEURD_KLANT | null */
  werkbonGoedkeuringStatus: string | null
  /** Zie isWerkbonAfgekeurd() - de caller berekent dit. */
  werkbonIsAfgekeurd: boolean
  /** "HH:mm" (24-uurs) of null. */
  plannedStart: string | null
  /** ISO dag van de entry, "yyyy-mm-dd" of langere ISO (alleen eerste 10 tekens tellen). */
  date: string
  /**
   * Geplande óf werkelijke duur in uren. NIET gebruiken als "ingevuld"-signaal:
   * `hours` is bij aanmaak al gevuld met de geplande duur, dus > 0 voor elke
   * gewone planning. Het "ingevuld"-signaal is `dayEntryStatus === 'ACTIEF'`.
   */
  hours: number
  now: Date
}

/**
 * Canonieke afkeur-predikaat. Regel uit de codebase: een werkbon is afgekeurd zodra
 * `goedkeuringStatus` op null staat EN er een afkeur-/weiger-tijdstempel is gezet.
 * Een opnieuw-ingediende werkbon heeft `goedkeuringStatus` terug op WACHT_OP_INTERN
 * met een achtergebleven (stale) `afgekeurdOp` en is dus NIET afgekeurd.
 */
export function isWerkbonAfgekeurd(input: {
  goedkeuringStatus: string | null
  afgekeurdOp: string | null
  klantWeigeringOp: string | null
}): boolean {
  return (
    input.goedkeuringStatus === null &&
    (input.afgekeurdOp !== null || input.klantWeigeringOp !== null)
  )
}

const WACHT_GOEDKEURING_STATUSES = new Set<string>([
  'WACHT_OP_INTERN',
  'GOEDGEKEURD_INTERN',
  'WACHT_OP_KLANT',
])

/** Leidt de Amsterdamse wandklok-componenten van een instant af, DST-robuust. */
function amsterdamWallClock(now: Date): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now)

  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? ''

  const year = get('year')
  const month = get('month')
  const day = get('day')
  // Sommige runtimes geven "24" voor middernacht - normaliseer naar "00".
  const hourRaw = get('hour')
  const hour = hourRaw === '24' ? '00' : hourRaw
  const minute = get('minute')

  return {
    date: `${year}-${month}-${day}`,
    time: `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`,
  }
}

export function derivePlanningActieStatus(input: PlanningActieStatusInput): PlanningActieStatus {
  // Afkeur en indiening zijn werkbon-niveau en gaan vóór de dag-status.
  // 1. Afgekeurd (intern of door klant) → monteur moet corrigeren.
  if (
    input.werkbonIsAfgekeurd ||
    input.werkbonGoedkeuringStatus === 'AFGEKEURD_INTERN' ||
    input.werkbonGoedkeuringStatus === 'AFGEKEURD_KLANT'
  ) {
    return 'CORRIGEREN'
  }

  // 2. Heropend (zonder afkeur) → corrigeren.
  if (input.dayEntryStatus === 'HEROPEND') {
    return 'CORRIGEREN'
  }

  // 3. Klant akkoord → klaar (eindstation van de flow).
  if (input.werkbonGoedkeuringStatus === 'GOEDGEKEURD_KLANT') {
    return 'KLAAR'
  }

  // 4. Ingediend / in goedkeuringsflow (vanaf WACHT_OP_INTERN) of dag gesloten →
  //    uit handen van de monteur, wacht op goedkeuring.
  if (
    (input.werkbonGoedkeuringStatus !== null &&
      WACHT_GOEDKEURING_STATUSES.has(input.werkbonGoedkeuringStatus)) ||
    input.dayEntryStatus === 'GESLOTEN'
  ) {
    return 'WACHT_GOEDKEURING'
  }

  // 5. Werkelijke tijden staan erin → ingevuld. Signaal is dag-status ACTIEF,
  //    NIET `hours > 0` (zie input.hours-doc): hours is bij aanmaak al gevuld met
  //    de geplande duur, dus > 0 voor elke gewone planning. De server flipt
  //    GEPLAND → ACTIEF zodra er actuals worden opgeslagen.
  if (input.dayEntryStatus === 'ACTIEF') {
    return 'INGEVULD'
  }

  // 6. Resterend geval = GEPLAND: bepaal of de monteur nu moet invullen.
  const ams = amsterdamWallClock(input.now)
  const dateOnly = input.date.slice(0, 10)

  const m = input.plannedStart && /^(\d{1,2}):(\d{2})/.exec(input.plannedStart)
  const plannedHHmm = m ? `${m[1].padStart(2, '0')}:${m[2]}` : null

  // Beide kanten zero-padded → lexicale vergelijking == chronologische vergelijking.
  const passed = plannedHHmm
    ? `${dateOnly}T${plannedHHmm}` <= `${ams.date}T${ams.time}`
    : dateOnly <= ams.date

  return passed ? 'NU_INVULLEN' : 'KOMT_NOG'
}

export const PLANNING_ACTIE_STATUS_LABELS: Record<PlanningActieStatus, string> = {
  KOMT_NOG: 'Gepland',
  NU_INVULLEN: 'Nu invullen',
  INGEVULD: 'Ingevuld',
  CORRIGEREN: 'Corrigeren',
  WACHT_GOEDKEURING: 'Wacht op goedkeuring',
  KLAAR: 'Klaar',
}

// Badge-variant keys uit @streep/ui (alleen string-literals, geen UI-import).
export const PLANNING_ACTIE_STATUS_BADGE: Record<
  PlanningActieStatus,
  'secondary' | 'warning' | 'success' | 'info'
> = {
  KOMT_NOG: 'secondary',
  NU_INVULLEN: 'warning',
  INGEVULD: 'success',
  CORRIGEREN: 'warning',
  WACHT_GOEDKEURING: 'info',
  KLAAR: 'success',
}

export interface PlanningActieTelling {
  status: PlanningActieStatus
  count: number
}

// Telregel-buckets in vaste volgorde. KOMT_NOG hoort NIET in de telregel (er is
// niets te doen). Alleen non-zero buckets verschijnen.
const TELREGEL_VOLGORDE: ReadonlyArray<PlanningActieStatus> = [
  'NU_INVULLEN',
  'CORRIGEREN',
  'INGEVULD',
  'WACHT_GOEDKEURING',
  'KLAAR',
]

export function summarizePlanningActieStatuses(
  statuses: ReadonlyArray<PlanningActieStatus>,
): PlanningActieTelling[] {
  const counts = new Map<PlanningActieStatus, number>()
  for (const status of statuses) {
    counts.set(status, (counts.get(status) ?? 0) + 1)
  }

  const result: PlanningActieTelling[] = []
  for (const status of TELREGEL_VOLGORDE) {
    const count = counts.get(status) ?? 0
    if (count > 0) {
      result.push({ status, count })
    }
  }
  return result
}

export interface WerkbonActieStatusDayInput {
  /** GEPLAND | ACTIEF | GESLOTEN | HEROPEND */
  status: string
  plannedStart: string | null
  /** ISO dag van de entry. */
  date: string
  hours: number
}

export interface WerkbonActieStatusInput {
  dayEntries: ReadonlyArray<WerkbonActieStatusDayInput>
  werkbonGoedkeuringStatus: string | null
  afgekeurdOp: string | null
  klantWeigeringOp: string | null
  now: Date
}

// Werkbon-headline: meest actiegerichte dag-status wint. Afkeur/goedkeuring zijn
// werkbon-breed (alle dagen geven dan dezelfde uitkomst); de prioriteit arbitreert
// in de praktijk alleen tussen de losse dag-staten NU_INVULLEN/INGEVULD/KOMT_NOG.
const WERKBON_FOLD_PRIORITEIT: ReadonlyArray<PlanningActieStatus> = [
  'CORRIGEREN',
  'NU_INVULLEN',
  'INGEVULD',
  'WACHT_GOEDKEURING',
  'KLAAR',
  'KOMT_NOG',
]

/**
 * Eén werkbon-status uit alle dag-entries. Retourneert `null` als er geen dagen
 * zijn (caller valt terug op de operationele werkbon-status).
 */
export function deriveWerkbonActieStatus(
  input: WerkbonActieStatusInput,
): PlanningActieStatus | null {
  if (input.dayEntries.length === 0) return null
  const werkbonIsAfgekeurd = isWerkbonAfgekeurd({
    goedkeuringStatus: input.werkbonGoedkeuringStatus,
    afgekeurdOp: input.afgekeurdOp,
    klantWeigeringOp: input.klantWeigeringOp,
  })
  const perDag = input.dayEntries.map((de) =>
    derivePlanningActieStatus({
      dayEntryStatus: de.status,
      werkbonGoedkeuringStatus: input.werkbonGoedkeuringStatus,
      werkbonIsAfgekeurd,
      plannedStart: de.plannedStart,
      date: de.date,
      hours: de.hours,
      now: input.now,
    }),
  )
  for (const status of WERKBON_FOLD_PRIORITEIT) {
    if (perDag.includes(status)) return status
  }
  return perDag[0] // onbereikbaar (alle statussen staan in de prioriteitslijst), typesafe
}
